<template lang="pug">
.gym-map-wrapper(:class="{collapsed: collapseFilters}")
  .gym-map(:class="{collapsed: collapseFilters}")
    .map-container(ref="mapContainer")
  RogueButton.add-gym-button(@click="$emit('addGym')") {{ __('Add Your Gym') }}
</template>
<script setup lang="ts">
import 'mapbox-gl/dist/mapbox-gl.css'
import type { IGym } from '@rogue/universal'
import { onMounted, onUnmounted, ref } from 'vue'
import { formatGymAddress } from './gymHelpers'
import { useRogue } from '@/js/services/Rogue'
import { loadScript } from '@/js/helpers/scripts'
import { APPROX_GYM_CIRCLE_RADIUS_METERS } from './gymConstants'

type MapBounds = {
  minLng : number,
  minLat : number,
  maxLng : number,
  maxLat : number,
}

const rogue = useRogue()

const mapContainer = ref<HTMLElement>()
const mapLoaded = ref(false)

const props = defineProps<{
  defaultPosition: number[],
  collapseFilters: boolean,
}>()

const emit = defineEmits<{ (e: 'viewGymDetails', gymId: string): void, (e: 'addGym'): void }>()

const GYMS = 'gyms'
const APPROX_GYM_ZIPS = 'approx-gym-zips'
const CLUSTER = 'cluster-circle'
const CLUSTER_COUNT = 'cluster-count'
const ROGUE_FACILITY = 'unclustered-rogue-facility'
const FEATURED_GYM = 'unclustered-featured-gym'
const APPROX_GYM = 'unclustered-approx-gym'
const APPROX_GYM_CIRCLES = 'unclustered-approx-gym-circles'
const ROGUE_FACILITY_MARKER = 'rogue-facility-marker'
const FEATURED_GYM_MARKER = 'featured-gym-marker'
const APPROX_GYM_MARKER = 'approx-gym-marker'
const hasPointCountFilter = ['all',
  ['has', 'point_count'],
]
const rogueFacilityFilter = ['all',
  ['!', ['has', 'point_count']],
  ['==', true, ['get', 'shareAddress']],
  ['!=', true, ['get', 'isFeatured']],
]
const featuredGymFilter = ['all',
  ['!', ['has', 'point_count']],
  ['==', true, ['get', 'shareAddress']],
  ['==', true, ['get', 'isFeatured']],
]
const approxGymFilter = ['all',
  ['!', ['has', 'point_count']],
  ['!=', true, ['get', 'shareAddress']],
]

const bounds = ref<MapBounds>({
  minLng : 0,
  minLat : 0,
  maxLng : 0,
  maxLat : 0,
})

let map: any

const initialData = {
  type     : 'FeatureCollection',
  features : [],
}

function updateBounds() {
  if (!map) return

  const mapBounds = map.getBounds()
  bounds.value = {
    minLng : mapBounds._sw.lng,
    minLat : mapBounds._sw.lat,
    maxLng : mapBounds._ne.lng,
    maxLat : mapBounds._ne.lat,
  }
}

function updateData(gyms: (IGym & { _id?: string })[]) {
  if (!mapLoaded.value) {
    setTimeout(() => {
      updateData(gyms)
    }, 200)

    return
  }

  if (map && map.getSource(GYMS)) {
    const geoJsonData = {
      type     : 'FeatureCollection',
      features : gyms
        .map(gym => ({
          type     : 'Feature',
          geometry : {
            type        : 'Point',
            coordinates : [gym.longitude, gym.latitude],
          },
          properties: {
            gymId        : gym._id,
            gymName      : gym.name,
            gymType      : gym.type,
            address      : gym.shareAddress ? formatGymAddress(gym.address) : undefined,
            phone        : gym.phone,
            isFeatured   : gym.isFeatured,
            shareAddress : gym.type !== 'Home Gym' && !!gym.shareAddress,
          },
        })),
    }

    map.getSource(GYMS).setData(geoJsonData)

    const zipData = {
      type     : 'FeatureCollection',
      features : [...new Set(gyms.filter(gym => gym.type === 'Home Gym' || !gym.shareAddress).map(gym => ({
        type     : 'Feature',
        geometry : {
          type        : 'Point',
          coordinates : [gym.longitude, gym.latitude],
        },
      })))],
    }

    map.getSource(APPROX_GYM_ZIPS).setData(zipData)

    updateBounds()
  }

  // Close any open popups
  const popups = document.getElementsByClassName('mapboxgl-popup')
  if (popups.length) {
    for (let i = 0; i < popups.length; i += 1) {
      popups[i].remove()
    }
  }
}

function centerMap(coordinates: number[]) {
  if (map) {
    map.easeTo({
      center : coordinates,
      zoom   : 11,
    })
  }
}

function fitBounds(newBounds: number[]) {
  if (map) {
    map.fitBounds(newBounds, {
      padding  : 20,
      duration : 300,
    })
  }
}

function getMapCenter(): number[] | null {
  if (map) {
    const center = map.getCenter()

    return [center.lng, center.lat]
  }

  return null
}

function resizeMap() {
  if (map) {
    map.resize()
  }
}

onMounted(() => {
  loadScript({ script: 'https://api.mapbox.com/mapbox-gl-js/v3.25.0/mapbox-gl.js', async: true }, rogue).then(() => {
    // @ts-ignore
    window.mapboxgl.accessToken = rogue.keys.mapbox?.mapboxGlJs

    // @ts-ignore
    map = new window.mapboxgl.Map({
      container          : mapContainer.value!,
      style              : 'mapbox://styles/mapbox/dark-v11',
      center             : props.defaultPosition,
      zoom               : 4,
      maxZoom            : 14,
      attributionControl : false,
    })

    map.on('load', () => {
      map.addSource(GYMS, {
        type           : 'geojson',
        data           : initialData,
        cluster        : true,
        clusterMaxZoom : 14, // Max zoom to cluster points on
        clusterRadius  : 30, // Radius of each cluster when clustering points (defaults to 50)
      })

      map.addSource(APPROX_GYM_ZIPS, {
        type    : 'geojson',
        data    : initialData,
        cluster : false,
      })

      map.loadImage('https://assets.roguefitness.com/w_25,q_auto,f_auto/Rogue%20Equipped%20Gyms%20Map/Pin%20Icons/png/Map_Home_Gym_pxswi6.png', (error: any, image: any) => {
        map.addImage(ROGUE_FACILITY_MARKER, image)
      })
      map.loadImage('https://assets.roguefitness.com/w_25,q_auto,f_auto/Rogue%20Equipped%20Gyms%20Map/Pin%20Icons/png/Map_Rogue_Facility_wyp7mv.png', (error: any, image: any) => {
        map.addImage(FEATURED_GYM_MARKER, image)
      })
      map.loadImage('https://assets.roguefitness.com/w_22,q_auto,f_auto/Rogue%20Equipped%20Gyms%20Map/Pin%20Icons/png/Home_Gym_Icon_ygvsfv.png', (error: any, image: any) => {
        map.addImage(APPROX_GYM_MARKER, image)
      })

      // @ts-ignore
      map.addControl(new window.mapboxgl.NavigationControl({
        showCompass: false,
      }))

      // @ts-ignore
      map.addControl(new window.mapboxgl.GeolocateControl({
        showUserLocation: false,
      }))

      // Documentational on mapbox zoom level meters/pixel https://docs.mapbox.com/help/glossary/zoom-level/
      // Currently showing a zircle around zip codes with a radius of about 1.5km
      map.addLayer({
        id      : 'zip-circle',
        type    : 'circle',
        source  : APPROX_GYM_ZIPS,
        minzoom : 11,
        paint   : {
          // Circle radius with zoom-dependent scaling
          'circle-radius': [
            'interpolate',
            ['exponential', 2],
            ['zoom'],
            11, APPROX_GYM_CIRCLE_RADIUS_METERS / 29.277,
            22, APPROX_GYM_CIRCLE_RADIUS_METERS / 0.014,
          ],
          'circle-color'        : '#3887be',
          'circle-opacity'      : 0.9,
          'circle-stroke-width' : 2,
          'circle-stroke-color' : '#1278b7',
        },
      })

      map.addLayer({
        id     : CLUSTER,
        type   : 'circle',
        source : GYMS,
        filter : hasPointCountFilter,
        paint  : {
          'circle-color'  : '#CDCDCD',
          'circle-radius' : 15,
        },
      })

      map.addLayer({
        id     : CLUSTER_COUNT,
        type   : 'symbol',
        source : GYMS,
        filter : hasPointCountFilter,
        layout : {
          'text-field' : ['get', 'point_count_abbreviated'],
          'text-size'  : 12,
        },
        paint: {
          'text-color': '#000',
        },
      })

      function createUnclusterLayer(layerId: string, markerId: string, filter: any) {
        return {
          id     : layerId,
          type   : 'symbol',
          source : GYMS,
          filter,
          layout : {
            'icon-anchor' : markerId === APPROX_GYM_MARKER ? 'center' : 'bottom',
            'icon-image'  : markerId,
          },
        }
      }

      map.addLayer({
        id     : APPROX_GYM_CIRCLES,
        type   : 'circle',
        source : GYMS,
        filter : approxGymFilter,
        paint  : {
          'circle-color'  : '#CDCDCD',
          'circle-radius' : 15,
        },
      })

      map.addLayer({
        id     : APPROX_GYM,
        type   : 'symbol',
        source : GYMS,
        filter : approxGymFilter,
        layout : {
          'text-field' : '1',
          'text-size'  : 12,
        },
        paint: {
          'text-color': '#000',
        },
      })

      map.addLayer(createUnclusterLayer(ROGUE_FACILITY, ROGUE_FACILITY_MARKER, rogueFacilityFilter))
      map.addLayer(createUnclusterLayer(FEATURED_GYM, FEATURED_GYM_MARKER, featuredGymFilter))

      // inspect a cluster on click
      map.on('click', CLUSTER, (e: any) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: [CLUSTER],
        })
        const clusterId = features[0].properties.cluster_id
        map.getSource(GYMS).getClusterExpansionZoom(
          clusterId,
          (err: any, zoom: number) => {
            if (err) return

            map.easeTo({
              center: features[0].geometry.coordinates,
              zoom,
            })
          }
        )
      })

      // When a click event occurs on a feature in the unclustered gym layers, open a popup at
      // the location of the feature, with description HTML from its properties.
      function handleGymClick(e: any) {
        const coordinates = e.features[0].geometry.coordinates.slice()
        const id = e.features[0].properties.gymId
        const name = e.features[0].properties.gymName
        const type = e.features[0].properties.gymType
        const address = e.features[0].properties.address
        const phone = e.features[0].properties.phone
        const isHomeGym = type === 'Home Gym'
        const shareAddress = !isHomeGym && e.features[0].properties.shareAddress

        const offset = [0, 0] // [x, y] offset in pixels

        // Convert the coordinates to screen pixels
        const point = map.project(coordinates)

        // Add the offset
        const offsetPoint = [
          point.x + offset[0],
          point.y + offset[1],
        ]

        // Convert back to geographic coordinates
        const offsetCoordinates = map.unproject(offsetPoint)

        // Move to the offset position
        map.easeTo({
          center   : offsetCoordinates,
          duration : 300,
        })

        // Ensure that if the map is zoomed out such that
        // multiple copies of the feature are visible, the
        // popup appears over the copy being pointed to.
        if (['mercator', 'equirectangular'].includes(map.getProjection().name)) {
          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
          }
        }

        let gymInfoHtml = ''
        if (address && shareAddress) {
          gymInfoHtml += `<div class="gym-info">${address}</div>`
        }
        if (phone && isHomeGym) {
          gymInfoHtml += `<div class="gym-info">${phone}</div>`
        }
        if (!shareAddress) {
          gymInfoHtml += '<div class="gym-info"><b>Note:</b>&nbsp;<i>Location is approximate to protect privacy.</i></div>'
        }

        const markerHeight = !shareAddress ? 30 : 28
        const markerWidth = !shareAddress ? 30 : 25
        const popupOffsets = !shareAddress
          ? markerHeight / 2
          : {
            top            : [0, 0],
            'top-left'     : [0, 0],
            'top-right'    : [0, 0],
            bottom         : [0, -markerHeight],
            'bottom-left'  : [0, -markerHeight],
            'bottom-right' : [0, -markerHeight],
            left           : [markerWidth / 2, -(markerHeight / 2)],
            right          : [-(markerWidth / 2), -(markerHeight / 2)],
          }

        // @ts-ignore
        new window.mapboxgl.Popup({
          maxWidth : '315px',
          offset   : popupOffsets,
        })
          .setLngLat(coordinates)
          .setHTML(
            `<div class="popup-content">
              <h5 class="popup-gym-name">${name}</h5>
              <div class="popup-gym-type">${type}</div>
              ${gymInfoHtml}
              <a class="mobile-details-link" href="#">View Gym Details</a>
            </div>
            <hr class="popup-divider" />
            <div class="popup-actions">
              <button class="popup-gym-details-btn">View Gym Details</button>
            </div>`
          )
          .addTo(map)

        const showDetailButton = document.querySelector('.popup-gym-details-btn')
        showDetailButton?.addEventListener('click', (event) => {
          event.preventDefault()
          emit('viewGymDetails', id)
        })
        const mobileShowDetailButton = document.querySelector('.popup-content .mobile-details-link')
        mobileShowDetailButton?.addEventListener('click', (event) => {
          event.preventDefault()
          emit('viewGymDetails', id)
        })
      }

      map.on('click', ROGUE_FACILITY, handleGymClick)
      map.on('click', FEATURED_GYM, handleGymClick)
      map.on('click', APPROX_GYM, handleGymClick)

      function showPointerCursor() {
        map.getCanvas().style.cursor = 'pointer'
      }
      function showDefaultCursor() {
        map.getCanvas().style.cursor = ''
      }

      map.on('mouseenter', CLUSTER, showPointerCursor)
      map.on('mouseleave', CLUSTER, showDefaultCursor)
      map.on('mouseenter', ROGUE_FACILITY, showPointerCursor)
      map.on('mouseleave', ROGUE_FACILITY, showDefaultCursor)
      map.on('mouseenter', FEATURED_GYM, showPointerCursor)
      map.on('mouseleave', FEATURED_GYM, showDefaultCursor)
      map.on('mouseenter', APPROX_GYM, showPointerCursor)
      map.on('mouseleave', APPROX_GYM, showDefaultCursor)

      map.on('moveend', () => {
        updateBounds()
      })

      mapLoaded.value = true
    })
  })
})

onUnmounted(() => {
  map?.remove()
  map = undefined
})

defineExpose({
  updateData,
  centerMap,
  fitBounds,
  getMapCenter,
  resizeMap,
  bounds,
})
</script>
<style lang="scss" scoped>
.gym-map-wrapper {
  background-color: $color-black-light;
  flex: 1;
  z-index: 0;

  @include not-desktop() {
    flex-grow: 0;
    flex-shrink: 0;

    &.collapsed {
      flex-grow: 1;
      flex-shrink: 1;
    }
  }

  .gym-map {
    min-height: 900px;
    position: relative;
    height: 100%;
    display: block;

    @include not-desktop() {
      min-height: auto;
      width: 100%;
      &:not(.collapsed) {
        height: auto;
        aspect-ratio: 75 / 46;
        max-height: 33vh;
      }

      &.collapsed {
        aspect-ratio: unset;
      }
    }
  }
}

.map-container {
  width: 100%;
  height: 100%;
}

.add-gym-button {
  bottom: $space-l;
  display: none;
  margin-left: $space-m;
  // bottom amount ($space-l) plus the height of the button (50px)
  margin-top: -82px;
  padding-left: $space-xxl;
  padding-right: $space-xxl;
  position: sticky;
  width: fit-content;
  z-index: 1;

  @include all-desktop() {
    display: block;
  }
}
</style>
<style lang="scss">
.map-container {
  .mapboxgl-ctrl-logo {
    display: none !important;
  }

  .mapboxgl-popup-content {
    background: $color-gray-light;
    padding: 0;
  }

  .mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
    border-top-color: $color-gray-light;
  }

  .mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip,
  .mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
    border-bottom-color: $color-gray-light;
  }

  .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
    border-left-color: $color-gray-light;
  }

  .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
    border-right-color: $color-gray-light;
  }

  .mapboxgl-popup-close-button {
    color: $color-black;
    font-size: $size-xxl;
    line-height: $size-s;
    padding: $space-s $space-xs;
  }

  .popup-content {
    padding: $space-s;

    @include all-desktop() {
      padding-bottom: $space-xs;
    }
  }

  .popup-divider {
    border: none;
    border-bottom: 1px solid $color-gray;
    margin: 0;

    @include not-desktop() {
      display: none;
    }
  }

  .popup-actions {
    padding: $space-xs $space-s $space-s;
    width: 100%;

    @include not-desktop() {
      display: none;
    }
  }

  .popup-gym-name {
    font-family: $font-base;
    font-size: $size-s;
    line-height: $size-s;
    margin-bottom: $space-xxs;
    margin-right: $space-m;
    text-transform: none;
  }

  .popup-gym-type {
    color: $color-black-light;
    font-size: $size-xxs;
    font-weight: bold;
    line-height: $size-xxs;
    margin-bottom: $space-xxs;
  }

  .gym-info {
    font-size: $size-xxs;
    line-height: 15px;
    margin-bottom: $space-xxs;

    &:last-of-type {
      @include all-desktop() {
        margin-bottom: 0;
      }
    }
  }

  .popup-gym-details-btn {
    background-color: $color-black;
    border: none;
    color: $color-gray-light;
    padding: $space-xs;
    width: 100%;

    &:hover {
      background: $color-black-light;
      cursor: pointer;
    }
  }

  .mobile-details-link {
    color: $color-black;
    display: none;
    font-size: $size-xxs;
    line-height: $size-xxs;
    text-decoration: underline;

    @include not-desktop() {
      display: block;
    }
  }

  .mapboxgl-ctrl-top-right {
    @include not-desktop() {
      display: none;
    }
  }
}
</style>
